import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Moment } from 'moment';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page,
  Toolbar,
  ExcelExport,
  Resize,
  DetailRow,
  SortSettingsModel,
  PagerComponent,
  SortEventArgs
} from '@syncfusion/ej2-react-grids';
import { ItemModel, ClickEventArgs } from '@syncfusion/ej2-navigations';
import { SwitchComponent } from '@syncfusion/ej2-react-buttons';
import { IWebinarsGridProps } from '../../../interfaces/props/IWebinarsListProps';
import { EXCEL_EXPORT_ACTION_ID } from '../../../common/constants/gridActionId.constants';
import { IWebinar } from '../../../common/model/webinar.model';
import * as gridUtils from '../../../utils/grid.utils';
import * as pagerUtils from '../../../utils/pager.utils';
import * as sortUtils from '../../../utils/sort.utils';
import * as dateHelpers from '../../../helpers/date.helpers';
import './webinarsGrid.scss';

type WebinarsGridPropsType = IWebinarsGridProps & RouteComponentProps & WithTranslation;

class WebinarsGrid extends Component<WebinarsGridPropsType> {
  public gridComponent: GridComponent = null;
  public toolbarOptions: ItemModel[] = [];
  public sortOptions: SortSettingsModel = null;

  public pagerComponent: PagerComponent = null;

  private readonly CREATE_WEBINAR_ACTION_ID = 'createWebinar';

  public constructor(props: WebinarsGridPropsType) {
    super(props);

    const { sortField, sortDirection, i18n } = this.props;

    this.toolbarOptions = [{
      tooltipText: i18n.t('syncfusion.grid.Excelexport'),
      id: EXCEL_EXPORT_ACTION_ID,
      prefixIcon: 'icon icon-file-excel-o'
    }];
    this.toolbarOptions.push({
      tooltipText: i18n.t('webinarsList.actions.createWebinar'),
      id: this.CREATE_WEBINAR_ACTION_ID,
      template: `
          <button type="button" class="e-tbar-btn e-tbtn-txt e-control e-btn e-lib">
            <span class="icon icon-webinar-list"></span>
            <span class="ml-1 icon icon-app-item-add"></span>
          </button>
        `
    });

    this.sortOptions = {
      columns: [{ field: sortField, direction: sortDirection }]
    };
  }

  public componentDidUpdate(prevProps: WebinarsGridPropsType) {
    gridUtils.manageSpinner(this.gridComponent, prevProps.isFetching, this.props.isFetching);
    gridUtils.manageSorting(this.gridComponent, prevProps.isFetching, this.props.isFetching, this.props.sortField, this.props.sortDirection);
  }

  public onToolbarClick(args: ClickEventArgs) {
    if (args.item.id === EXCEL_EXPORT_ACTION_ID) {
      this.gridComponent.excelExport();
    }

    if (args.item.id === this.CREATE_WEBINAR_ACTION_ID) {
      this.props.history.push('/webinar/new');
    }
  }

  public onGridComponentDataBound() {
    const { currentPage, pageSize, totalCount, searchDate, i18n } = this.props;

    pagerUtils.refreshPager(this.pagerComponent, currentPage, pageSize, totalCount, searchDate, i18n);
  }

  public onGridComponentActionBegin(args: SortEventArgs) {
    if (args.requestType === 'sorting') {
      if (args.columnName === 'date') {
        this.gridComponent.getColumnByField('date').sortComparer = (reference, comparer) =>
          sortUtils.gridDateSortComparer(reference as unknown as Moment, comparer as unknown as Moment, args.direction);
      } else {
        this.gridComponent.getColumnByField(args.columnName).sortComparer = (reference, comparer) =>
          sortUtils.gridSortComparer(reference, comparer, args.direction);
      }

      gridUtils.manageSortingChange(args, this.props.sortField, this.props.sortDirection, this.props.onSortingChange);
    }
  }

  public getNameText(name: string): string {
    let value = '---';
    if (name !== undefined && name !== null && name.trim().length > 0)
      value = name;

    return value;
  }

  public getNameColumnTemplate(webinar: IWebinar): JSX.Element {
    return <span className='e-link' onClick={() => this.props.history.push(`/webinar/${webinar.id}/edit`)}>{this.getNameText(webinar.name)}</span>;
  }

  public getDescriptionColumnTemplate(webinar: IWebinar): JSX.Element {
    return <span>{(webinar.description.length > 255 ? webinar.description.substring(0, 255) : webinar.description)}</span>;
  }

  public getDateLabel(webinar: IWebinar): string {
    return webinar.date && dateHelpers.toShortDate(webinar.date, true);
  }

  public getActiveColumnTemplate(webinar: IWebinar): JSX.Element {
    return <SwitchComponent key={webinar.id} readOnly={true} checked={webinar.active} disabled={true} />;
  }

  public onPagerComponentClick(e: any) {
    if (e.currentPage !== this.props.currentPage) {
      this.props.onCurrentPageChange(e.currentPage);
    }
  }

  public onPageSizesDropdownChange(e: any) {
    if (e.pageSize !== this.props.pageSize) {
      this.props.onPageSizeChange(e.pageSize);
    }
  }

  public render() {
    const { webinars, i18n } = this.props;

    return (
      <div className='webinars-grid'>
        <GridComponent
          dataSource={webinars}
          toolbar={this.toolbarOptions}
          allowSorting={true}
          allowPaging={true}   /* Si no se incluye este paginador (oculto por css), no se muestra el dropdown en el customizado */
          allowExcelExport={true}
          allowResizing={true}
          sortSettings={this.sortOptions}
          pageSettings={{ pageSize: pagerUtils.getMaxPageSize() }}
          locale={i18n.language}
          ref={(grid: GridComponent) => this.gridComponent = grid}
          toolbarClick={(args) => this.onToolbarClick(args)}
          dataBound={() => this.onGridComponentDataBound()}
          actionBegin={(args) => this.onGridComponentActionBegin(args as SortEventArgs)}
        >
          <ColumnsDirective>
            <ColumnDirective
              headerText={this.props.i18n.t('webinar.name')}
              field='name'
              width='100'
              template={(webinar: IWebinar) => this.getNameColumnTemplate(webinar)}
            />
            <ColumnDirective
              headerText={this.props.i18n.t('webinar.speakers')}
              field='speakers'
              width='100'
            />
            <ColumnDirective
              headerText={this.props.i18n.t('webinar.date')}
              field='date'
              width='30'
              valueAccessor={(_field, data) => this.getDateLabel(data as IWebinar)}
            />
            <ColumnDirective
              headerText={this.props.i18n.t('webinar.description')}
              field='description'
              textAlign='Center'
              width='75'
              allowSorting={false}
              template={(webinar: IWebinar) => this.getDescriptionColumnTemplate(webinar)}
            />
            <ColumnDirective
              headerText={this.props.i18n.t('webinar.active')}
              field='active'
              width='50'
              template={(webinar: IWebinar) => this.getActiveColumnTemplate(webinar)}
              allowSorting={false}
            />
          </ColumnsDirective>
          <Inject services={[Sort, Page, Toolbar, ExcelExport, Resize, DetailRow]} />
        </GridComponent>
        {webinars.length ?
          <PagerComponent
            id='webinarsPagerComponent'
            name='WebinarsPagerComponent'
            pageSize={pagerUtils.getDefaultPageSize()}
            pageCount={pagerUtils.getPageCount()}
            pageSizes={pagerUtils.getPageSizes()}
            ref={(pagerComponent) => this.pagerComponent = pagerComponent}
            click={(e) => this.onPagerComponentClick(e)}
            dropDownChanged={(e) => this.onPageSizesDropdownChange(e)}
          />
          :
          null
        }
      </div>
    );
  }
}

export default withRouter(withTranslation()(WebinarsGrid));
